import { useState, useEffect, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Trash2, Upload, Link, Image } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Squircle } from "@/components/ui/squircle";
import { ImageCropModal } from "@/components/image-crop-modal";

const logoFormSchema = z.object({
  logoUrl: z.string().url("Please enter a valid URL").optional().or(z.literal("")),
});

type LogoFormData = z.infer<typeof logoFormSchema>;

type LogoConfig = {
  type: 'organization';
  id: number;
  name: string;
  apiPath: string;
  uploadPath: string;
  queryKeys: string[];
} | {
  type: 'event';
  id: number;
  name: string;
  apiPath: string;
  uploadPath: string;
  queryKeys: string[];
};

interface LogoModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  config: LogoConfig;
  currentLogoUrl?: string | null;
}

export function LogoModal({
  isOpen,
  onOpenChange,
  config,
  currentLogoUrl,
}: LogoModalProps) {
  const [activeTab, setActiveTab] = useState<"url" | "upload">("upload");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [fileForCropping, setFileForCropping] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const form = useForm<LogoFormData>({
    resolver: zodResolver(logoFormSchema),
    defaultValues: {
      logoUrl: currentLogoUrl || "",
    },
  });

  // Reset form when currentLogoUrl changes or modal opens/closes
  useEffect(() => {
    if (isOpen) {
      form.reset({
        logoUrl: currentLogoUrl || "",
      });
      setSelectedFile(null);
      setCropModalOpen(false);
      setFileForCropping(null);
      setActiveTab("upload");
      setIsUploading(false);
    }
  }, [currentLogoUrl, isOpen, form]);

  const updateLogoMutation = useMutation({
    mutationFn: async (logoUrl: string | null) => {
      const response = await apiRequest("PATCH", config.apiPath, {
        logoUrl,
      });
      return response.json();
    },
    onSuccess: () => {
      config.queryKeys.forEach(key => {
        queryClient.invalidateQueries({ queryKey: [key] });
      });
      toast({
        title: "Success",
        description: `${config.type === 'organization' ? 'Organisation' : 'Event'} logo updated successfully`,
      });
      onOpenChange(false);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update logo",
        variant: "destructive",
      });
    },
  });

  const handleFileUpload = async (file: File) => {
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append("logo", file);

      const response = await fetch(config.uploadPath, {
        method: "POST",
        body: formData,
        credentials: "include",
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Upload failed");
      }

      const result = await response.json();
      updateLogoMutation.mutate(result.logoUrl);
    } catch (error: any) {
      toast({
        title: "Upload Failed",
        description: error.message || "Failed to upload logo",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      validateAndSetFile(file);
    }
  };

  const handleFileDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setDragActive(false);
    
    const file = event.dataTransfer.files?.[0];
    if (file) {
      validateAndSetFile(file);
    }
  };

  const validateAndSetFile = (file: File) => {
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: "Invalid file type",
        description: "Only JPEG, PNG, WebP, and SVG files are allowed.",
        variant: "destructive",
      });
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Maximum file size is 5MB.",
        variant: "destructive",
      });
      return;
    }

    // For SVG files, skip cropping (they're vector-based)
    if (file.type === 'image/svg+xml') {
      setSelectedFile(file);
      handleFileUpload(file);
      return;
    }

    // For other image types, open crop modal
    setFileForCropping(file);
    setCropModalOpen(true);
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    setDragActive(false);
  };

  const handleCropComplete = (croppedFile: File) => {
    setSelectedFile(croppedFile);
    setCropModalOpen(false);
    setFileForCropping(null);
    handleFileUpload(croppedFile);
  };

  const handleCropCancel = () => {
    setCropModalOpen(false);
    setFileForCropping(null);
  };

  const onSubmit = (data: LogoFormData) => {
    const logoUrl = data.logoUrl?.trim() || null;
    updateLogoMutation.mutate(logoUrl);
  };

  const handleRemoveLogo = () => {
    updateLogoMutation.mutate(null);
  };

  const isLoading = updateLogoMutation.isPending || isUploading;

  const entityType = config.type === 'organization' ? 'organisation' : 'event';
  const entityTypeCapitalized = config.type === 'organization' ? 'Organisation' : 'Event';

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-lg  backdrop-blur-xl border-white/20 shadow-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Image className="h-5 w-5" />
              Manage {entityTypeCapitalized} Logo
            </DialogTitle>
            <DialogDescription>
              {config.type === 'organization' ? (
                <>
                  Upload or set a logo URL for "{config.name}". This logo will be displayed by default on all event signup pages for this organisation, unless a specific event has its own logo uploaded.

                </>
              ) : (
                <>
                  Upload or set a logo URL for "{config.name}". This logo will be displayed on the signup page for this specific event, overriding the organisation's default logo.

                </>
              )}
            </DialogDescription>
          </DialogHeader>
          
          {currentLogoUrl && (
            <div className="flex items-center justify-between p-4 bg-white/50 rounded-lg border border-white/30">
              <div className="flex items-center gap-3">
                <Squircle 
                  src={currentLogoUrl} 
                  alt={`${config.name} Logo`} 
                  size={40}
                />
                <span className="text-sm text-slate-600">Current Logo</span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRemoveLogo}
                disabled={isLoading}
                className="bg-red-50/80 border-red-200 text-red-600 hover:bg-red-100/80"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Remove
              </Button>
            </div>
          )}
          
          <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as "url" | "upload")} className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-white/40 backdrop-blur-md">
              <TabsTrigger value="upload" className="flex items-center gap-2">
                <Upload className="h-4 w-4" />
                Upload
              </TabsTrigger>
              <TabsTrigger value="url" className="flex items-center gap-2">
                <Link className="h-4 w-4" />
                URL
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="upload" className="space-y-4">
              <div
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors bg-white/30 backdrop-blur-sm ${
                  dragActive 
                    ? "border-blue-500 bg-blue-50/50" 
                    : "border-slate-300 hover:border-slate-400"
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleFileDrop}
              >
                {selectedFile ? (
                  <div className="space-y-2">
                    <Image className="h-8 w-8 mx-auto text-green-600" />
                    <p className="font-medium text-sm">{selectedFile.name}</p>
                    <p className="text-xs text-slate-500">
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedFile(null)}
                      disabled={isLoading}
                      className="bg-white/60 backdrop-blur-md border-white/40"
                    >
                      Remove
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Upload className="h-12 w-12 mx-auto text-slate-400" />
                    <div>
                      <p className="font-medium text-slate-600 mb-4">
                        Drop your logo here, or choose an image to upload (max 5MB)
                      </p>
                      <Button
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isLoading}
                        className="bg-white/60 backdrop-blur-md border-white/40 hover:bg-white/70"
                      >
                        {isLoading ? "Uploading..." : "Select Image"}
                      </Button>
                    </div>
                    <p className="text-xs text-slate-500">
                      Supports JPEG, PNG, WebP, SVG up to 5MB
                      <br />
                      You'll be able to crop your image to a square before uploading
                    </p>
                  </div>
                )}
              </div>
              
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp,image/svg+xml"
                onChange={handleFileSelect}
                className="hidden"
              />
            </TabsContent>
            
            <TabsContent value="url" className="space-y-4">
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="logoUrl"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Logo URL</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="https://example.com/logo.png"
                            {...field}
                            className="bg-white/60 backdrop-blur-md border-white/40"
                            autoComplete="off"
                            disabled={isLoading}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="flex gap-2 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => onOpenChange(false)}
                      className="flex-1 bg-white/40 backdrop-blur-md border-white/30"
                      disabled={isLoading}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={isLoading}
                      className="flex-1 bg-blue-600/80 backdrop-blur-sm text-white hover:bg-blue-700/90 border border-blue-500/30"
                    >
                      {isLoading ? "Updating..." : "Update Logo"}
                    </Button>
                  </div>
                </form>
              </Form>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
      
      <ImageCropModal
        isOpen={cropModalOpen}
        onOpenChange={handleCropCancel}
        file={fileForCropping}
        onCropComplete={handleCropComplete}
      />
    </>
  );
}

// Helper functions to create configs for different entity types
export function createOrganizationLogoConfig(organisationId: number, organisationName: string): LogoConfig {
  return {
    type: 'organization',
    id: organisationId,
    name: organisationName,
    apiPath: `/api/organisations/${organisationId}/logo`,
    uploadPath: `/api/organisations/${organisationId}/logo/upload`,
    queryKeys: ['organisations']
  };
}

export function createEventLogoConfig(eventId: number, eventName: string): LogoConfig {
  return {
    type: 'event',
    id: eventId,
    name: eventName,
    apiPath: `/api/events/${eventId}/logo`,
    uploadPath: `/api/events/${eventId}/logo/upload`,
    queryKeys: ['events', `/api/events/${eventId}`]
  };
}